import { findLastIndex, findFirstIndex } from 'utils/functional';
import { getShipperOrdersSkin } from 'utils/styled/Skins';
import { finishedStatuses } from 'utils/journey';
import { calculateStopScore } from 'redux/modules/journeys/stop-status-log';
import { calculateStopProblems } from 'redux/modules/journeys/stop-problems';
import { computeShipper } from 'redux/modules/shipper-orders/selectors';

const CANCELLED_STATUSES = ['CANCELLED'];

const isStopInProgress = ({ status }) => ['IN_PROGRESS'].includes(status);
const isStopPending = ({ status }) => ['PENDING'].includes(status);

const computeStops = (journey) => {
  const currentStopIndex = findLastIndex(journey.stops)(isStopInProgress);
  const pendingStopIndex = findFirstIndex(journey.stops)(isStopPending);

  const stops = journey.stops || [];

  return stops.map((stop, index) => {
    const { status, shipments } = stop;
    const score = calculateStopScore(status, shipments);

    const isCancelled = CANCELLED_STATUSES.includes(stop.status);

    const problems = calculateStopProblems(stop, index, {
      stopScore: score,
      currentStopIndex,
      pendingStopIndex,
    });

    const isAggregated = Object.keys(stop?.shipper_orders || []).length > 1 || !!stop?.store;

    return {
      ...stop,
      score,
      stopScore: score,
      isCancelled,
      problems,
      isAggregated,
    };
  });
};

const computeShipperOrder = ({ shipper, ...shipper_order }) => {
  return {
    ...shipper_order,
    shipper: computeShipper(shipper),
  };
};

/**
 * @param {object}
 * @returns {array}
 */
const computeShipperOrders = ({ shipper_orders = [] }) => shipper_orders.map(computeShipperOrder);

/**
 * @param {object}
 * @returns {object}
 */
const computeMargin = ({ shipper_orders, ...journey }) => {
  const cost = (type = 'net') => Number(journey[`${type}_full_cost`]);
  const price = (type = 'net') =>
    shipper_orders.reduce((acc, so) => acc + Number(so[`${type}_full_price`]), 0);
  const margin = (type = 'net') => Math.round((price(type) - cost(type)) * 100) / 100;

  const net_margin = margin('net');
  const gross_margin = margin('gross');

  return {
    net_margin,
    gross_margin,
  };
};

/**
 * @param {object} journey
 * @returns {object}
 */
const computeProblematic = (journey) => {
  const { has_supported_vehicle, stops = [] } = journey;

  const isInvalidVehicle = !has_supported_vehicle;
  const isProblematic = stops.some(({ problems = {} }) =>
    Object.keys(problems).some((problem) => !!problems[problem])
  );

  return {
    ...journey,
    isInvalidVehicle,
    isProblematic: isProblematic || isInvalidVehicle,
  };
};

/**
 * @param {object} journey
 * @returns {object}
 */
const mapJourneyForComputedProperties = (journey) => ({
  ...journey,
  ...getShipperOrdersSkin(journey.shipper_orders),
  ...computeMargin(journey),
  shipper_orders: computeShipperOrders(journey),
  stops: computeStops(journey),
  recurring_orders: journey.shipper_orders
    .map(({ recurring_order }) => recurring_order)
    .filter((recurring_order) => !!recurring_order),
  isFinished: finishedStatuses.includes(journey.status),
  isGroupage: journey.shipper_orders.length > 1,
});

/**
 * @param {array} journeys
 * @returns {array}
 */
export const computeJourneys = (journeys) => {
  return journeys.map(mapJourneyForComputedProperties).map(computeProblematic);
};

export {
  computeStops,
  computeShipperOrder,
  computeShipperOrders,
  computeMargin,
  computeProblematic,
  mapJourneyForComputedProperties,
};
